import type { PublishedMapLayer } from '../services/project-map-manifest.service';
import type { MapFeatureLink } from '../models/map-feature-link';
import { draftMapFeatureLink } from './design-context';

export type MapCoordinate = [number, number];
export type MapBounds = [number, number, number, number];
export interface MapFeatureReference { catalogRevision: number; layerId: string; sourceVersionId: string; sublayerKey: string; featureId: string; }
export interface MapFeatureCandidate {
  layer: PublishedMapLayer;
  featureId: string;
  distance: number;
  drawOrder: number;
  fields: { label: string; value: string }[];
  /** Everything the feature carries, for telling look-alikes apart in the list. */
  properties?: Record<string, unknown>;
  /** Popup field names in the reviewer's order, for the same purpose. */
  fieldNames?: string[];
  /** GeoJSON (WGS84) from the lookup, outlined on the map while the row is hovered or open. */
  geometry?: unknown;
  /** List row title and the line under it ("Layer · 120 ft"). */
  label?: string;
  meta?: string;
  /** Rows of one feature split by value (launcher multi-value fields); '' or absent otherwise. */
  variant?: string;
}
export interface InspectionLayer { layer: PublishedMapLayer; drawOrder: number; }
export interface InspectionQuery {
  projectId: string; catalogRevision: number; layers: InspectionLayer[];
  point: MapCoordinate; bbox: MapBounds; resolution: number;
}

export function validMapBounds(bounds: unknown): bounds is MapBounds {
  return Array.isArray(bounds) && bounds.length === 4 && bounds.every(Number.isFinite)
    && bounds[0] >= -180 && bounds[2] <= 180 && bounds[1] >= -85.051129 && bounds[3] <= 85.051129
    && bounds[0] <= bounds[2] && bounds[1] <= bounds[3];
}

export function boundsIntersect(a: MapBounds, b: MapBounds): boolean {
  return a[0] <= b[2] && a[2] >= b[0] && a[1] <= b[3] && a[3] >= b[1];
}

function mercator(coordinate: unknown): MapCoordinate {
  if (!Array.isArray(coordinate) || coordinate.length < 2 || !coordinate.slice(0, 2).every(Number.isFinite)
    || Math.abs(coordinate[0]) > 180 || Math.abs(coordinate[1]) > 85.051129) throw new Error('Invalid feature coordinates');
  const rad = Math.PI / 180;
  return [6378137 * coordinate[0] * rad, 6378137 * Math.log(Math.tan(Math.PI / 4 + coordinate[1] * rad / 2))];
}

/** Exact bounded geometry hit testing, in Web Mercator CSS pixels. Rotation preserves distance. */
export function geometryDistance(geometry: any, point: MapCoordinate, resolution: number, budget: { remaining: number }): number {
  if (!Number.isFinite(resolution) || resolution <= 0) throw new Error('Invalid map resolution');
  const origin = mercator(point);
  const position = (value: unknown): MapCoordinate => {
    if (--budget.remaining < 0) throw new Error('Feature inspection is too complex');
    const projected = mercator(value);
    return [(projected[0] - origin[0]) / resolution, (projected[1] - origin[1]) / resolution];
  };
  const segment = (a: MapCoordinate, b: MapCoordinate): number => {
    const dx = b[0] - a[0]; const dy = b[1] - a[1]; const length = dx * dx + dy * dy;
    const t = length ? Math.max(0, Math.min(1, -(a[0] * dx + a[1] * dy) / length)) : 0;
    return Math.hypot(a[0] + t * dx, a[1] + t * dy);
  };
  const line = (values: unknown, ring = false): { distance: number; inside: boolean } => {
    if (!Array.isArray(values) || values.length < (ring ? 4 : 2)) throw new Error('Invalid feature line');
    let previous = position(values[0]); let distance = Infinity; let inside = false;
    for (let i = 1; i < values.length; i++) {
      const next = position(values[i]);
      distance = Math.min(distance, segment(previous, next));
      if ((previous[1] > 0) !== (next[1] > 0)
        && 0 < previous[0] + (-previous[1]) * (next[0] - previous[0]) / (next[1] - previous[1])) inside = !inside;
      previous = next;
    }
    if (ring && (values[0][0] !== values[values.length - 1][0] || values[0][1] !== values[values.length - 1][1])) throw new Error('Invalid feature ring');
    return { distance, inside };
  };
  const polygon = (values: unknown): number => {
    if (!Array.isArray(values) || !values.length) throw new Error('Invalid feature polygon');
    const exterior = line(values[0], true); let distance = exterior.distance; let inHole = false;
    for (let i = 1; i < values.length; i++) {
      const hole = line(values[i], true); distance = Math.min(distance, hole.distance); inHole ||= hole.inside;
    }
    return exterior.inside && !inHole ? 0 : distance;
  };
  const many = (values: unknown, read: (value: any) => number): number => {
    if (!Array.isArray(values) || !values.length || values.length > 75000) throw new Error('Invalid feature geometry');
    let distance = Infinity;
    for (const value of values) distance = Math.min(distance, read(value));
    return distance;
  };
  const visit = (value: any, depth = 0): number => {
    if (!value || depth > 8 || --budget.remaining < 0) throw new Error('Feature inspection is too complex');
    switch (value.type) {
      case 'Point': return Math.hypot(...position(value.coordinates));
      case 'MultiPoint': return many(value.coordinates, item => Math.hypot(...position(item)));
      case 'LineString': return line(value.coordinates).distance;
      case 'MultiLineString': return many(value.coordinates, item => line(item).distance);
      case 'Polygon': return polygon(value.coordinates);
      case 'MultiPolygon': return many(value.coordinates, polygon);
      case 'GeometryCollection': return many(value.geometries, item => visit(item, depth + 1));
      default: throw new Error('Unsupported feature geometry');
    }
  };
  return visit(geometry);
}

/** A map layer as far as its drawing range needs it (ArcGIS minScale / maxScale: 0 is no limit) */
export interface DrawRangeLayer {
  visible?: boolean;
  minScale?: number;
  maxScale?: number;
  layers?: { toArray(): DrawRangeLayer[] } | DrawRangeLayer[] | null;
}

/**
 * A design layer can be clicked wherever it is drawn: its own scale range, and for a group, any visible child drawing
 * at this scale. Not the published zoom range: vector layers draw at every zoom, and raster layers enlarge their top
 * tiles past maxZoom; only a raster layer's lowest tiles set where it stops drawing.
 */
export function layerDrawsAtScale(layer: DrawRangeLayer | null | undefined, scale: number): boolean {
  if (!layer || layer.visible === false || !Number.isFinite(scale)) return false;
  if (layer.minScale > 0 && scale > layer.minScale) return false;
  if (layer.maxScale > 0 && scale < layer.maxScale) return false;
  const children = Array.isArray(layer.layers) ? layer.layers : layer.layers?.toArray?.();
  return children ? children.some(child => layerDrawsAtScale(child, scale)) : true;
}

export function inspectionTolerance(layer: PublishedMapLayer, geometryType?: string): number {
  const geometry = /Point$/.test(geometryType || '') ? 'point' : /LineString$/.test(geometryType || '') ? 'line'
    : /Polygon$/.test(geometryType || '') ? 'polygon' : null;
  const symbols = [...layer.style.symbols, ...(layer.style.uniqueValues?.values || []).map(item => item.symbol)]
    .filter(symbol => !geometry || symbol.geometry === geometry);
  // ArcGIS vector symbols use points; raster symbols use pixels. Include the
  // larger footprint plus 8 CSS pixels of tap slop, bounded by StyleV1 maxima.
  return Math.min(136, Math.max(8, ...symbols.map(symbol =>
    8 + ((symbol.geometry === 'point' ? (symbol.size ?? 8) : 0) + (symbol.strokeWidth ?? 1)) * 96 / 72 / 2)));
}

function ringContainsPoint(ring: number[][], [x, y]: MapCoordinate): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i]; const [xj, yj] = ring[j];
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

function polygonContainsPoint(rings: number[][][], point: MapCoordinate): boolean {
  if (!rings.length || !ringContainsPoint(rings[0], point)) return false;
  for (let i = 1; i < rings.length; i++) if (ringContainsPoint(rings[i], point)) return false;
  return true;
}

/** Shoelace area of a ring, in cos-scaled degrees: a consistent ordering, not a true area. */
function ringArea(ring: number[][], latitude: number): number {
  const scale = Math.cos(latitude * Math.PI / 180);
  let twice = 0;
  for (let i = 1; i < ring.length; i++) {
    twice += ring[i - 1][0] * scale * ring[i][1] - ring[i][0] * scale * ring[i - 1][1];
  }
  return Math.abs(twice) / 2;
}

/** Exterior-ring area of the polygon (or MultiPolygon member) that actually contains the point. */
function containingArea(geometry: { type: string; coordinates: unknown }, point: MapCoordinate): number {
  const polygons = (geometry.type === 'Polygon' ? [geometry.coordinates] : geometry.coordinates) as number[][][][];
  let best = Infinity;
  for (const polygon of polygons) if (polygonContainsPoint(polygon, point)) best = Math.min(best, ringArea(polygon[0], point[1]));
  return best;
}

function isPolygonGeometry(geometry: unknown): geometry is { type: string; coordinates: unknown } {
  const type = (geometry as { type?: string })?.type;
  return type === 'Polygon' || type === 'MultiPolygon';
}

function byDrawOrder(left: MapFeatureCandidate, right: MapFeatureCandidate): number {
  return right.drawOrder - left.drawOrder || left.distance - right.distance
    || left.layer.id.localeCompare(right.layer.id) || left.featureId.localeCompare(right.featureId);
}

/**
 * Rank precise geometry hits: visual draw order first, then proximity, then stable identity.
 * A tap inside a polygon means that polygon: the containing polygons come first, smallest
 * exterior ring first (a bed inside a limit-of-work outline), and only lines or points within
 * half their own tap tolerance stay listed. Without containment the old rules apply.
 */
export function rankMapFeatureCandidates(candidates: MapFeatureCandidate[], point: MapCoordinate): MapFeatureCandidate[] {
  const containing = candidates.filter(candidate => isPolygonGeometry(candidate.geometry) && candidate.distance === 0);
  if (!containing.length) return [...candidates].sort(byDrawOrder);
  const area = new Map(containing.map(candidate => [candidate, containingArea(candidate.geometry as { type: string; coordinates: unknown }, point)]));
  const underFinger = candidates.filter(candidate => !isPolygonGeometry(candidate.geometry)
    && candidate.distance <= inspectionTolerance(candidate.layer, (candidate.geometry as { type?: string })?.type) / 2);
  return [...containing.sort((left, right) => area.get(left) - area.get(right) || byDrawOrder(left, right)),
    ...underFinger.sort(byDrawOrder)];
}

/**
 * The popup fields this feature has a value for. A layer's popup lists every
 * field any feature carries (an irrigation layer: zone, pipe size, block), so
 * a field this feature leaves empty is left off its card rather than shown as —.
 */
export function candidateFields(layer: PublishedMapLayer, properties: unknown): MapFeatureCandidate['fields'] {
  const values = properties && typeof properties === 'object' && !Array.isArray(properties) ? properties : {};
  return layer.style.popupFields.flatMap(field => {
    const value = Object.prototype.hasOwnProperty.call(values, field.field) ? values[field.field] : null;
    const text = value === null || value === undefined ? '' : typeof value === 'object' ? JSON.stringify(value) : String(value);
    if (!text.trim()) return [];
    return [{ label: field.label, value: text.length > 1024 ? `${text.slice(0, 1024)}…` : text }];
  });
}

export function featureIssueData(query: InspectionQuery, candidate: MapFeatureCandidate): {
  projectId: string; entity_type: 'project'; entity_id: string; latitude: number; longitude: number;
  map_feature_ref: MapFeatureReference; map_feature_link: MapFeatureLink;
} {
  const map_feature_ref: MapFeatureReference = { catalogRevision: query.catalogRevision, layerId: candidate.layer.id,
    sourceVersionId: candidate.layer.sourceVersionId, sublayerKey: candidate.layer.sublayerKey, featureId: candidate.featureId };
  return { projectId: query.projectId, entity_type: 'project', entity_id: query.projectId,
    longitude: query.point[0], latitude: query.point[1], map_feature_ref,
    map_feature_link: draftMapFeatureLink(query, candidate, { latitude: query.point[1], longitude: query.point[0] }) };
}

/** A feature's row title: its first popup field that has a value, else "Feature n". */
export function featureLabel(fields: { value: string }[], position: number): string {
  const first = fields.map(field => field.value?.trim()).find(value => value && value !== '—');
  return first || `Feature ${position + 1}`;
}

/** Mobile's rule for look-alike rows: the first popup field that differs, or "(n of m)". */
export function titleLookAlikes<T extends { label?: string; properties?: Record<string, unknown>; fieldNames?: string[] }>(
  items: T[], groupOf: (item: T) => string): void {
  const groups = new Map<string, T[]>();
  for (const item of items) {
    const key = `${groupOf(item)}|${item.label}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(item);
  }
  const idField = /(^|[_\s-])(id|uuid|guid|gid|fid|objectid|oid)$/i;
  const idValue = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-|^[0-9a-f]{16,}$/i;
  const shownValue = (value: unknown): string => (typeof value === 'string' && value.trim()) || (typeof value === 'number' ? String(value) : '');
  groups.forEach(group => {
    if (group.length < 2) return;
    const name = group[0].label;
    const telling = (group[0].fieldNames || []).find(field => {
      if (idField.test(field)) return false;
      const shown = group.map(item => shownValue(item.properties?.[field]));
      if (shown.some(value => idValue.test(value))) return false;
      return shown.some(Boolean) && new Set(shown).size > 1 && !shown.every(value => value === name);
    });
    group.forEach((item, position) => {
      const shown = telling ? shownValue(item.properties?.[telling]) : '';
      item.label = shown && shown !== name ? `${name} · ${shown}` : `${name} (${position + 1} of ${group.length})`;
    });
  });
}

/** Distance wording shared with mobile: feet under 1,000, then miles. */
export function distanceText(metres: number | undefined): string {
  if (!Number.isFinite(metres) || metres < 0) return '';
  const feet = metres * 3.28084;
  if (feet < 1) return 'right here';
  if (feet < 1000) return `${Math.round(feet)} ft`;
  return `${(feet / 5280).toFixed(1)} mi`;
}

/** GeoJSON geometry to an ArcGIS geometry in WGS84; null for anything else. */
export function geoJsonToArcGis(geometry: unknown): { type: string } & Record<string, unknown> | null {
  const g = geometry as { type?: string; coordinates?: unknown };
  const sr = { wkid: 4326 };
  switch (g?.type) {
    case 'Point': { const [x, y] = g.coordinates as number[]; return { type: 'point', x, y, spatialReference: sr }; }
    case 'MultiPoint': return { type: 'multipoint', points: g.coordinates as number[][], spatialReference: sr };
    case 'LineString': return { type: 'polyline', paths: [g.coordinates as number[][]], spatialReference: sr };
    case 'MultiLineString': return { type: 'polyline', paths: g.coordinates as number[][][], spatialReference: sr };
    case 'Polygon': return { type: 'polygon', rings: g.coordinates as number[][][], spatialReference: sr };
    case 'MultiPolygon': return { type: 'polygon', rings: (g.coordinates as number[][][][]).flat(), spatialReference: sr };
    default: return null;
  }
}

/** The outline drawn over a hovered or opened feature: the app's blue, sized per geometry. */
export function featureHighlightSymbol(geometryType: string): Record<string, unknown> {
  const blue = [16, 54, 214];
  return geometryType === 'point' || geometryType === 'multipoint'
    ? { type: 'simple-marker', style: 'circle', size: 16, color: [...blue, 0.2], outline: { color: [...blue, 1], width: 2 } }
    : geometryType === 'polyline'
      ? { type: 'simple-line', color: [...blue, 1], width: 4 }
      : { type: 'simple-fill', color: [...blue, 0.15], outline: { color: [...blue, 1], width: 2 } };
}
